import { Component, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { AccountService } from '@frontendlib/common/services/account.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { type AlertButtonConfig } from '@modules/alert/types/config';
import { ContainerDndService } from '@modules/common/services/container-dnd.service';
import { StitchService } from '@modules/common/services/stitch.service';
import { BaseSidebarComponent } from '@modules/common/components/base-sidebar/base-sidebar.component';
import { type SidebarSharedRow } from '@modules/common/components/knowledge-items-rail/shared-placeholder.component';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { isStitchItemSummary } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { Application } from '@modules/common/types/application';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { workspaceColor, workspacePalette } from '@modules/common/utils/workspace-color';
import { DragData, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { FilesService } from '@modules/files/services/files.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { FoldersService } from '@modules/files/services/folders.service';
import { File } from '@modules/files/types/file';
import { Folder } from '@modules/files/types/folder';
import { CROSS_WORKSPACE_DROP_TEXT } from '@modules/files/types/cross-workspace-drop-text';
import { VirtualFolder } from '@modules/files/types/virtual-folder';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { Space } from '@modules/settings/types/space';
import { Team } from '@modules/settings/types/team';
import { map, switchMap, takeUntil, timeout } from 'rxjs/operators';

const RAIL_IDENTITY_TIMEOUT_MS = 10_000;

@Component({
  selector: 'app-files-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.less'],
  standalone: false,
})
export class FilesSideBarComponent
  extends BaseSidebarComponent<Folder, File, VirtualFolder>
  implements OnInit, OnDestroy
{
  application = Application.files;
  sidebarSplitViewKey = SidebarSplitViewKey.files;
  selfDragDataTypes = [DragDataTypes.file, DragDataTypes.folder];

  // Make enum accessible in template
  public readonly SidebarSplitViewKey = SidebarSplitViewKey;

  public baseItems: SidebarItem[] = [
    { title: 'Files', id: 'all_files', icon: 'sapphire-file' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
  ];

  public readonly primaryItems: SidebarItem[] = [
    { title: 'My Files', id: 'all_files', icon: 'sapphire-file' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
  ];
  public readonly teamPrimaryItems = this.baseItems.filter(({ id }) => ['all_files', 'flagged'].includes(id));
  public readonly moreItems = this.baseItems.filter(({ id }) => !['all_files', 'flagged'].includes(id));
  public readonly sharedItems: readonly SidebarSharedRow[] = [
    { title: 'Shared Files', icon: 'sapphire-file' },
    { title: 'Shared Folders', icon: 'file-folder' },
  ];

  protected readonly workspaceColor = workspaceColor;
  protected readonly workspacePalette = workspacePalette;
  protected readonly myWorkspaceExpanded = signal(true);
  protected readonly moreExpanded = signal(false);
  protected readonly teamWorkspaceExpanded = signal(true);
  protected readonly currentTeamWorkspaceExpanded = signal(true);
  protected readonly teamMoreExpanded = signal(false);
  protected identityResolved = false;
  protected currentTeamWorkspace: Space | undefined;
  protected currentMemberTenant: Team | undefined;
  protected currentTeamTenant: Team | undefined;
  protected currentTeamWorkspaceInitials = '';

  private readonly copyDropService = inject(ContainerDndService);
  private readonly copyStitchService = inject(StitchService);
  private readonly accountService = inject(AccountService);
  private readonly crossWorkspaceAlertService = inject(AlertService);
  private readonly filesTenantScopeService = inject(FilesTenantScopeService);
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly spacesService = inject(SpacesService);

  constructor(
    injector: Injector,
    private filesService: FilesService,
    private foldersService: FoldersService,
  ) {
    super(injector);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    if (!this.spaceContextStore.insideTeam()) {
      // A private profile has no team to resolve, so this rail settles on its FIRST paint with an empty
      // list: identity resolved, no team, `resolveMemberIdentity(null)`. Deliberately NOT
      // `degradeToPersonal()` — that is the FAILED-lookup arm (invariant 3), and on a personal context
      // `AppTenantScopeService.degradeToPersonal()` self-guards on this very condition and returns, so
      // it marked nothing degraded AND never released the identity gate. `insideTeam()` is the seam's
      // one named place for the question (#1127 seam round 2, `SpaceContextStore.ts:132`), and it
      // carries the `&& !!contextId` half this rail's hand-rolled predicate omitted: a shared space
      // with no context id used to take the TEAM arm here and the personal arm in notes.
      this.setCurrentTeamWorkspace([], this.spaceContextStore.contextId());

      return;
    }

    this.accountService
      .getAccount(true)
      .pipe(
        switchMap((account) =>
          this.spacesService
            .listWithInvites({ archived: false, limit: 100 }, { account })
            .pipe(map(({ items }) => items)),
        ),
        timeout({ first: RAIL_IDENTITY_TIMEOUT_MS }),
        takeUntil(this.alive),
      )
      .subscribe({
        next: (items) => this.setCurrentTeamWorkspace(items, this.spaceContextStore.contextId()),
        error: () => this.degradeToPersonal(),
      });
  }

  private degradeToPersonal(): void {
    this.currentTeamWorkspace = undefined;
    this.currentMemberTenant = undefined;
    this.currentTeamTenant = undefined;
    this.currentTeamWorkspaceInitials = '';
    this.identityResolved = true;
    this.filesTenantScopeService.degradeToPersonal();
  }

  private setCurrentTeamWorkspace(items: Space[], contextId: string | undefined): void {
    this.currentTeamWorkspace = items.find(
      ({ currentUserStatus, id }) => currentUserStatus === 'accepted' && id === contextId,
    );
    this.currentMemberTenant = this.currentTeamWorkspace?.myMailboxTenantId
      ? new Team({ id: this.currentTeamWorkspace.myMailboxTenantId })
      : undefined;
    this.currentTeamTenant = this.currentTeamWorkspace ? new Team({ id: this.currentTeamWorkspace.id }) : undefined;
    this.filesTenantScopeService.resolveMemberIdentity(this.currentMemberTenant?.id ?? null);
    this.currentTeamWorkspaceInitials =
      this.currentTeamWorkspace?.title
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase() ?? '';
    this.identityResolved = true;
  }

  protected toggleCurrentTeamWorkspace(): void {
    this.currentTeamWorkspaceExpanded.update((expanded) => !expanded);
  }

  /**
   * Actions
   */

  public override dndDrop(
    dragData: DragData,
    sidebarFolder: VirtualFolder,
    index?: number,
    team: Team | undefined = this.team,
  ): void {
    const targetTenantId = team?.id ?? this.spaceContextStore.contextId();
    if (dragData.type === DragDataTypes.file) {
      const owners = new Set(dragData.data.map((item) => getStringField(item, 'accountId')));
      if (!dragData.data.length || owners.size !== 1 || !owners.has(targetTenantId)) {
        this.copyDropService.handleChildItemDrop(
          new Folder({ accountId: targetTenantId, title: 'Files' }),
          dragData,
          {
            containerType: DragDataTypes.folder,
            childItemType: DragDataTypes.file,
            childService: this.filesService,
            containerIdField: 'folderId',
            crossTenantCopySupported: true,
            cancelled: this.alive,
            isActive: () => !this.alive.isStopped,
            appStateService: this.stateService,
          },
          this.copyStitchService,
          this.crossWorkspaceAlertService,
        );
        return;
      }
      if (
        sidebarFolder === 'all_files' &&
        dragData.data.every(isStitchItemSummary) &&
        dragData.data.every(
          (item) => !Reflect.get(item, 'folderId') && !Reflect.get(item, 'deleted') && !Reflect.get(item, 'archived'),
        )
      )
        return;
    }
    // Only the two types this rail owns actions for reach the honest dialog. Any other draggable — a
    // task, note or event dragged out of a file's linked-info list — keeps the base rail's own drop
    // behaviour, which is where that type's answer lives; round 1 handed it this dialog instead, and
    // with it a LIVE Copy whose body is a file/folder if-else-if with no `else` (notes r18 F1).
    const ownsCrossWorkspaceDialog = dragData.type === DragDataTypes.file || dragData.type === DragDataTypes.folder;

    if (ownsCrossWorkspaceDialog && this.isCrossWorkspaceDrop(dragData, targetTenantId)) {
      this.crossWorkspaceAlertService.show({
        title: 'Copy, Move or Stitch?',
        body: 'What do we do with the dropped item?',
        note:
          dragData.type === DragDataTypes.folder
            ? CROSS_WORKSPACE_DROP_TEXT.folderNote
            : CROSS_WORKSPACE_DROP_TEXT.fileNote,
        rightButtons: this.getCrossWorkspaceDropButtons(dragData),
      });
      return;
    }

    super.dndDrop(dragData, sidebarFolder, index);
  }

  handleMove(dragData: DragData, sidebarFolder: VirtualFolder) {
    const ids = this.getIds(dragData.data);

    if (sidebarFolder === 'deleted') {
      if (dragData.type === DragDataTypes.file) {
        this.filesService.delete({ ids }, true);
      } else if (dragData.type === DragDataTypes.folder) {
        this.foldersService.delete({ ids }, true);
      }
      return;
    }

    // Restore trashed/archived items first — virtual-folder lists filter out archived/deleted
    // items, so applying the changes alone would leave them stuck in Trash/Archive.
    const restoreOptions = { skipArchiveRestore: sidebarFolder === 'archived' };

    if (dragData.type === DragDataTypes.file) {
      const { changes, message } = File.getChangesForVirtualFolder(sidebarFolder);
      moveItemsIntoContainer(
        this.filesService,
        dragData.data,
        () => this.filesService.bunchUpdate({ ids }, changes, { message }),
        restoreOptions,
      );
    } else if (dragData.type === DragDataTypes.folder) {
      const { changes, message } = Folder.getChangesForVirtualFolder(sidebarFolder);
      moveItemsIntoContainer(
        this.foldersService,
        dragData.data,
        () => this.foldersService.bunchUpdate({ ids }, changes, { message }),
        restoreOptions,
      );
    }
  }

  handleMore(item: File) {
    this.stateService.setMainView(item);
  }

  getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder) {
    const { changes } = File.getChangesForVirtualFolder(sidebarFolder);

    return new File({ ...changes, title: dragData.data[0]['title'] });
  }

  /**
   * All three actions are disabled, with the reason in the dialog's note and on every tooltip.
   *
   * Round 1 shipped a LIVE Copy here, but the backend has no two-tenant duplicate for files: both
   * `POST /files/duplicate` and `POST /folders/duplicate` look the source up under the DESTINATION
   * `space_id` only, so a cross-workspace Copy cannot find the item it was handed. An action the app
   * cannot perform is shown disabled with a reason, never live and never faked — the notebook rule
   * (Pavel, 2026-09-09). The buttons carry no `click` at all, so the dialog cannot fire a request even
   * if the disabled attribute is lost.
   */
  private getCrossWorkspaceDropButtons(dragData: DragData): AlertButtonConfig[] {
    return [
      { title: 'Cancel', close: true },
      {
        title: 'Copy',
        disabled: true,
        tooltip:
          dragData.type === DragDataTypes.folder
            ? CROSS_WORKSPACE_DROP_TEXT.folderCopyTooltip
            : CROSS_WORKSPACE_DROP_TEXT.fileCopyTooltip,
        close: true,
      },
      {
        title: 'Move',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.moveTooltip,
        close: true,
      },
      {
        title: 'Stitch',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.stitchTooltip,
        close: true,
      },
    ];
  }

  private isCrossWorkspaceDrop(dragData: DragData, targetTenantId: string | undefined): boolean {
    const sourceTenantId = getStringField(dragData.data[0], 'accountId');
    return sourceTenantId !== undefined && targetTenantId !== undefined && sourceTenantId !== targetTenantId;
  }
}
